import React, { useState } from 'react';
import { Event } from '@/hooks/useEvents';
import { Calendar, Clock, MapPin, Euro, Users, Bell, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import { fr, enUS, es, de, it, nl, pt, zhCN, ja } from 'date-fns/locale';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { LanguageCode, MENU_UI_TRANSLATIONS } from '@/constants/menuUITranslations';

interface EventDetailPopupProps {
  event: Event | null;
  isOpen: boolean;
  onClose: () => void;
  primaryColor?: string;
  restaurantId: string;
  restaurantName?: string;
  currentLanguage?: LanguageCode;
}

const getDateLocale = (lang: LanguageCode) => {
  const locales = { fr, en: enUS, es, de, it, nl, pt, zh: zhCN, ja };
  return locales[lang] || fr;
};

const EventDetailPopup: React.FC<EventDetailPopupProps> = ({ 
  event, 
  isOpen, 
  onClose, 
  primaryColor = '#8B5CF6',
  restaurantId,
  restaurantName,
  currentLanguage = 'fr'
}) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const t = MENU_UI_TRANSLATIONS[currentLanguage];
  const locale = getDateLocale(currentLanguage);

  if (!event) return null;

  const formatEventDate = (dateStr: string) => {
    try {
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return format(date, 'EEEE d MMMM yyyy', { locale });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (timeStr: string | null) => {
    if (!timeStr) return '';
    return timeStr.slice(0, 5);
  };

  const handleSubmitReminder = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast({
        title: t.invalidEmail,
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('newsletter_subscribers')
        .upsert({
          email: email.toLowerCase().trim(),
          restaurant_id: restaurantId,
          source: `event_reminder:${event.id}`,
          is_active: true
        }, {
          onConflict: 'email,restaurant_id'
        });

      if (error) throw error;

      setIsSubscribed(true);
      toast({
        title: `${t.reminderActivated} 🎉`,
        description: t.reminderConfirmation
      });
    } catch (error) {
      console.error('Error subscribing to reminder:', error);
      toast({
        title: t.errorOccurred,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setIsSubscribed(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold pr-6">{event.title}</DialogTitle>
        </DialogHeader>

        {event.image_url && (
          <div className="w-full h-48 rounded-lg overflow-hidden -mt-2">
            <img 
              src={event.image_url} 
              alt={event.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <div className="space-y-4">
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" style={{ color: primaryColor }} />
              <span className="capitalize">{formatEventDate(event.event_date)}</span>
            </div>
            {event.event_time && (
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" style={{ color: primaryColor }} />
                <span>
                  {formatTime(event.event_time)}
                  {event.end_time && ` - ${formatTime(event.end_time)}`}
                </span>
              </div>
            )}
          </div>

          {event.location && (
            <div className="flex items-center gap-2 text-sm">
              <MapPin className="h-4 w-4" style={{ color: primaryColor }} />
              <span>{event.location}</span>
            </div>
          )}

          <div className="flex flex-wrap gap-4 text-sm">
            {event.price && (
              <div className="flex items-center gap-2 font-semibold" style={{ color: primaryColor }}>
                <Euro className="h-4 w-4" />
                <span>{event.price}€</span>
              </div>
            )}
            {event.max_participants && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Users className="h-4 w-4" />
                <span>{event.max_participants} {t.places}</span>
              </div>
            )}
          </div>

          {event.description && (
            <p className="text-sm text-muted-foreground leading-relaxed">
              {event.description}
            </p>
          )}

          <div 
            className="rounded-lg p-4 mt-4"
            style={{ backgroundColor: `${primaryColor}10` }}
          >
            {isSubscribed ? (
              <div className="flex items-center gap-3 text-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm font-medium text-green-700">
                  {t.reminderActivated} {t.reminderConfirmation}
                </span>
              </div>
            ) : (
              <form onSubmit={handleSubmitReminder} className="space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <Bell className="h-4 w-4" style={{ color: primaryColor }} />
                  <span className="text-sm font-medium">{t.getReminderOnDay}</span>
                </div>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder={t.yourEmail}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1"
                    required
                  />
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    style={{ backgroundColor: primaryColor }}
                  >
                    {isSubmitting ? '...' : 'OK'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t.youWillReceiveNews} {restaurantName || ''}.
                </p>
              </form>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EventDetailPopup;
